import {HeadFC, PageProps} from "gatsby"
import {GatsbyImage} from "gatsby-plugin-image"
import {Fragment, useContext, useState} from "react"
import ContactUsForm from "~/components/Forms/ContactUsForm"
import Blank from "~/components/Sections/Blank/Blank"
import CaseHistorySection from "~/components/Sections/CaseHistorySection/CaseHistorySection"
import HeroSection from "~/components/Sections/HeroSection/HeroSection"
import Search from "~/components/Sections/Search/Search"
import Post from "~/components/Sections/Shelf/Post"
import Shelf, {ShelfSortCategories} from "~/components/Sections/Shelf/Shelf"
import Button from "~/components/UI/Button/Button"
import Gap from "~/components/UI/Gap/Gap"
import InputText from "~/components/UI/InputText/InputText"
import SectionContainer from "~/components/UI/SectionContainer/SectionContainer"
import Switch from "~/components/UI/Switch/Switch"
import MobileVideoPlayer from "~/components/UI/VideoPlayer/MobileVideoPlayer"
import TransitionContext from "~/contexts/TransitionContext"
import {useCasePostsQuery} from "~/queries/CasePostsQuery"
import {useLabelsQuery} from "~/queries/LabelsQuery"
import {useLinksQuery} from "~/queries/LinksQuery"
import {PostItem} from "~/types/PostItem"
import {generateQueryParams} from "~/utilities/queryParams"
import {SEO} from "../components/UI/SEO/SEO"
import SocialShare from "../components/UI/SocialShare/SocialShare"
import {PageContext} from "../types/PageContext"
import {isVideoMimeType} from "../utilities/mimeUtilities"
import "./CasePost.css"
import {MagazineSearchParam, MagazineSectionParam, SectionIndex, sectionsIndexToname} from "./MagazineTemplate"

type CaseHistoryContext = PageContext<
    Queries.CaseHistoryPostQuery["allContentfulContenutoCaseHistory"]["edges"][0]["node"],
    Queries.CaseHistoryPostQuery["allContentfulPaginaMagazine"]["edges"][0]["node"]
>

interface CasePostProps extends PageProps<any, CaseHistoryContext> {}

const CasePost: React.FC<CasePostProps> = props => {
    const casePostData = props.pageContext.data
    const magazinePageData = props.pageContext.secondaryData
    const links = useLinksQuery()
    const labels = useLabelsQuery()
    const [selectedSectionIndex, setSelectedSectionIndex] = useState<SectionIndex | number>(0)
    const [inputTextValue, setInputTextValue] = useState("")
    const [selectedSectionName, setSelectedSectionName] = useState<ShelfSortCategories | string>("")
    const transitionContenxt = useContext(TransitionContext)

    const casePosts: Array<PostItem> = []
    useCasePostsQuery().forEach(post => {
        if (casePostData.slug === post.slug) return
        casePosts.push(post)
    })

    const onInputChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputTextValue(event.currentTarget.value)
    }
    const onSwitchClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
        const selectedType = Number(event.currentTarget.value)
        setSelectedSectionIndex(selectedType)
        setSelectedSectionName(sectionsIndexToname(selectedType))
    }
    const onFormSubmitHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const queryParams = generateQueryParams(
            {name: MagazineSectionParam, value: selectedSectionName},
            {name: MagazineSearchParam, value: inputTextValue},
        )
        const url = `${links.magazine.url}?${queryParams}`
        transitionContenxt.navigateTo(url)
    }

    const showcase =
        casePostData.demoVideo && casePostData.demoVideo.url && isVideoMimeType(casePostData.demoVideo.mimeType) ? (
            <MobileVideoPlayer
                videoClass="secondary-player-tvr6"
                pauseVideo={false}
                videoSources={[
                    {
                        src: casePostData.demoVideo?.url,
                        mimeType: casePostData.demoVideo?.mimeType,
                    },
                ]}
            />
        ) : casePostData.demoImmagine &&
          casePostData.demoImmagine.gatsbyImageData &&
          casePostData.demoImmagine.title ? (
            <GatsbyImage
                alt={casePostData.demoImmagine.title}
                image={casePostData.demoImmagine?.gatsbyImageData}
                className="gatsby-image-tvr6"
                imgClassName="image-tvr6"
                loading="eager"
            />
        ) : undefined

    return (
        <Fragment>
            <div className="case-post-header-container-tvr6">
                <HeroSection
                    image={casePostData.image?.gatsbyImageData}
                    imageAlt={casePostData.image?.title}
                    className="case-post-hero-tvr6"
                    gatsbyImageClass="hero-gatsby-image-tvr6"
                >
                    {!casePostData.demoVideo?.url && !casePostData.demoImmagine?.gatsbyImageData && (
                        <Fragment>
                            <h3 className="title-on-hero-tvr6">{casePostData.title}</h3>
                        </Fragment>
                    )}
                </HeroSection>
                {casePostData.demoVideo &&
                casePostData.demoVideo.url &&
                isVideoMimeType(casePostData.demoVideo.mimeType) ? (
                    <MobileVideoPlayer
                        className="mobile-player-tvr6"
                        videoClass="video-tvr6"
                        pauseVideo={false}
                        videoSources={[
                            {
                                src: casePostData.demoVideo?.url,
                                mimeType: casePostData.demoVideo?.mimeType,
                            },
                        ]}
                    />
                ) : (
                    casePostData.demoImmagine &&
                    casePostData.demoImmagine.gatsbyImageData &&
                    casePostData.demoImmagine.title && (
                        <GatsbyImage
                            alt={casePostData.demoImmagine.title}
                            image={casePostData.demoImmagine?.gatsbyImageData}
                            className="mobile-hero-gatsby-image-tvr6"
                            imgClassName="mobile-hero-image-tvr6"
                            loading="eager"
                            objectFit="contain"
                        />
                    )
                )}
            </div>
            <SectionContainer>
                <Search title={magazinePageData?.titolo} startClosed>
                    <Gap centered style={{marginTop: "var(--gutter-4)"}}>
                        <Switch
                            key={magazinePageData?.allLabel}
                            selected={selectedSectionIndex === SectionIndex.index}
                            onSelect={onSwitchClickHandler}
                            value={SectionIndex.index}
                        >
                            {magazinePageData?.allLabel}
                        </Switch>
                        {magazinePageData?.sezioni?.map((section, index) => {
                            return (
                                <Switch
                                    key={section?.slug}
                                    selected={selectedSectionIndex === index + 1}
                                    onSelect={onSwitchClickHandler}
                                    value={index + 1}
                                >
                                    {section?.titolo}
                                </Switch>
                            )
                        })}
                    </Gap>
                    <form onSubmit={onFormSubmitHandler}>
                        <Gap centered>
                            <InputText
                                {...(magazinePageData?.searchInputPlaceholder
                                    ? {placeholder: magazinePageData?.searchInputPlaceholder}
                                    : {})}
                                onChange={onInputChangeValue}
                                value={inputTextValue}
                                style={{
                                    width: "min(30vw,500px)",
                                    minWidth: "240px",
                                    fontStyle: "italic",
                                }}
                            />
                            <Button color="green" type="submit">
                                {magazinePageData?.searchButtonLabel}
                            </Button>
                        </Gap>
                    </form>
                </Search>
            </SectionContainer>
            <SectionContainer>
                <Blank className="case-post-title-tvr6">
                    <SocialShare layout="case-history" />
                    <h3>{casePostData.title}</h3>
                    <div className="case-post-sub-title-tvr6">
                        <div className="block-1-tvr6">
                            {`${labels.customer}: `}
                            <span className="bold-tvr6">{casePostData.customer}</span>
                        </div>
                        <div className="block-2-tvr6">
                            {`${labels.project}: `}
                            <span className="bold-tvr6">{casePostData.project}</span>
                        </div>
                    </div>
                </Blank>
                <CaseHistorySection
                    className="first-heading-green-tvr6"
                    title={casePostData.presentazione?.titolo}
                    backgroundImage={casePostData.presentazione?.immagine?.gatsbyImageData}
                    backgroundAlt={casePostData.presentazione?.immagine?.title}
                >
                    {casePostData.presentazione?.contenuto?.raw}
                </CaseHistorySection>
                <CaseHistorySection
                    title={casePostData.storia?.titolo}
                    name={labels.history}
                    backgroundImage={casePostData.storia?.immagine?.gatsbyImageData}
                    backgroundAlt={casePostData.storia?.immagine?.title}
                >
                    {casePostData.storia?.contenuto?.raw}
                </CaseHistorySection>
                <CaseHistorySection
                    title={casePostData.obiettivo?.titolo}
                    name={labels.objective}
                    backgroundImage={casePostData.obiettivo?.immagine?.gatsbyImageData}
                    backgroundAlt={casePostData.obiettivo?.immagine?.title}
                >
                    {casePostData.obiettivo?.contenuto?.raw}
                </CaseHistorySection>
                <CaseHistorySection
                    title={casePostData.soluzione?.titolo}
                    name={labels.solution}
                    backgroundImage={casePostData.soluzione?.immagine?.gatsbyImageData}
                    backgroundAlt={casePostData.soluzione?.immagine?.title}
                    showcase={showcase}
                >
                    {casePostData.soluzione?.contenuto?.raw}
                </CaseHistorySection>
                <CaseHistorySection
                    title={casePostData.successo?.titolo}
                    name={labels.success}
                    backgroundImage={casePostData.successo?.immagine?.gatsbyImageData}
                    backgroundAlt={casePostData.successo?.immagine?.title}
                >
                    {casePostData.successo?.contenuto?.raw}
                </CaseHistorySection>
            </SectionContainer>
            <SectionContainer className="contact-us-section-tvr6">
                <ContactUsForm />
            </SectionContainer>
            <SectionContainer>
                <Shelf show={2} label={labels.moreProjects}>
                    {casePosts.map((item, index) => {
                        return (
                            <Post
                                key={index}
                                date={item.date}
                                title={item.title}
                                image={item.image}
                                category={item.category}
                                tags={item.tags}
                                description_short={item.description_short}
                                slug={item.slug}
                                type={item.type}
                                customer={item.customer}
                                project={item.project}
                            />
                        )
                    })}
                </Shelf>
            </SectionContainer>
        </Fragment>
    )
}

export default CasePost

export const Head: HeadFC<any, CaseHistoryContext> = props => {
    const casePostData = props.pageContext.data

    const links = useLinksQuery()

    return (
        <SEO
            title={`${casePostData.title} | ${casePostData.presentazione?.titolo}`}
            description={casePostData.description_short}
            language={props.pageContext.language}
            {...(casePostData.immagineSocialShare
                ? {image: casePostData.immagineSocialShare?.url, imageAlt: casePostData.immagineSocialShare?.title}
                : {image: casePostData.image?.url, imageAlt: casePostData.image?.title})}
            siteUrl={`${process.env.SITE_URL}${links.caseHistory.url}/${casePostData.slug}`}
        />
    )
}
